import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useNotifications } from './NotificationContext';
import './notifications.scss';

export default function NotificationToastStack() {
  const { toasts, dismissToast } = useNotifications();

  if (!toasts.length) return null;

  const latestToast = toasts[0];
  const pendingCount = toasts.length;
  const isBatch = pendingCount > 1;

  const handleNavigate = () => {
    latestToast.onAction?.();
    toasts.forEach((toast) => dismissToast(toast.id));
  };

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    dismissToast(latestToast.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate();
    }
  };

  return (
    <div className="notification-toast-stack" aria-live="polite">
      <div
        className="notification-card"
        role="button"
        tabIndex={0}
        onClick={handleNavigate}
        onKeyDown={handleKeyDown}
        aria-label="View online orders"
      >
        <div className="notification-card__accent" aria-hidden />

        <button
          type="button"
          className="notification-card__close"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <CloseIcon fontSize="small" />
        </button>

        <div className="notification-card__header">
          <div className="notification-card__icon-wrap">
            <ShoppingBagOutlinedIcon className="notification-card__icon" />
            <span className="notification-card__pulse" aria-hidden />
          </div>
          <div className="notification-card__headline">
            <span className="notification-card__eyebrow">
              {isBatch ? `${pendingCount} new online orders` : 'New online order'}
            </span>
            <h3 className="notification-card__title">
              {isBatch
                ? `${latestToast.orderCode ?? latestToast.title} + ${pendingCount - 1} more`
                : latestToast.orderCode ?? latestToast.title}
            </h3>
          </div>
        </div>

        {!isBatch && (
          <div className="notification-card__meta">
            {latestToast.tableOrRoom && (
              <span className="notification-card__chip">
                <PersonOutlineIcon fontSize="inherit" />
                {latestToast.tableOrRoom}
              </span>
            )}
            {typeof latestToast.itemCount === 'number' && (
              <span className="notification-card__chip">
                <ReceiptLongOutlinedIcon fontSize="inherit" />
                {latestToast.itemCount} {latestToast.itemCount === 1 ? 'item' : 'items'}
              </span>
            )}
            {typeof latestToast.totalCost === 'number' && (
              <span className="notification-card__chip notification-card__chip--amount">
                ₹{latestToast.totalCost.toFixed(0)}
              </span>
            )}
          </div>
        )}

        {isBatch && (
          <p className="notification-card__batch-note">
            Latest: {latestToast.body}
            {pendingCount > 1 ? ` · ${pendingCount - 1} more waiting` : ''}
          </p>
        )}

        <div className="notification-card__footer">
          <span>{latestToast.actionLabel ?? 'View online orders'}</span>
          <ArrowForwardIcon fontSize="small" />
        </div>
      </div>
    </div>
  );
}
